import { useState, useEffect } from "react";
import { UiModal } from "../../../../Components/Shared/UiModal";

const EMPTY_FORM = {
  zoneId: "",
  minWeight: "",
  maxWeight: "",
  builtyRate: "",
  coachRate: "",
  addaDelivery: "",
};

export default function OutStationRatesModal({
  isOpen,
  onClose,
  onSubmit,
  editData,
  zones = [],
}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      return;
    }

    if (editData) {
      setFormData({
        zoneId: editData.zone_id || "",
        minWeight: editData.min_weight || "",
        maxWeight: editData.max_weight || "",
        builtyRate: editData.builty_rate || "",
        coachRate: editData.coach_rate || "",
        addaDelivery: editData.adda_delivery || "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, editData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "zoneId") {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      const regex = /^\d*\.?\d*$/;
      if (regex.test(value) || value === "") {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.zoneId) {
      newErrors.zoneId = "Zone selection is required";
    }

    if (!formData.minWeight) {
      newErrors.minWeight = "Minimum Weight is required";
    }

    if (!formData.maxWeight) {
      newErrors.maxWeight = "Maximum Weight is required";
    }

    if (formData.minWeight && formData.maxWeight) {
      const min = parseFloat(formData.minWeight);
      const max = parseFloat(formData.maxWeight);
      if (min >= max) {
        newErrors.maxWeight =
          "Maximum Weight must be greater than Minimum Weight";
      }
    }

    if (!formData.builtyRate) {
      newErrors.builtyRate = "Builty Rate is required";
    }

    if (!formData.coachRate) {
      newErrors.coachRate = "Coach Rate is required";
    }

    if (!formData.addaDelivery) {
      newErrors.addaDelivery = "Adda Delivery is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        zone_id: formData.zoneId,
        min_weight: parseFloat(formData.minWeight),
        max_weight: parseFloat(formData.maxWeight),
        builty_rate: parseFloat(formData.builtyRate),
        coach_rate: parseFloat(formData.coachRate),
        adda_delivery: parseFloat(formData.addaDelivery),
      };
      onSubmit(submitData);
    }
  };

  const isEditMode = Boolean(editData);
  const fieldClass = (name) =>
    `ui-input-field ${errors[name] ? "border-brand-error" : ""}`;

  return (
    <UiModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Delivery Management"
      title={
        isEditMode ? "Edit Out-Station Base Rate" : "Add Out-Station Base Rate"
      }
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="ui-btn-cancel min-w-[108px]"
          >
            Cancel
          </button>
          <button type="submit" className="ui-btn-save min-w-[108px]">
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Rate details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Out Station Zone Name <span className="text-red-500">*</span>
            </label>
            <select
              name="zoneId"
              value={formData.zoneId}
              onChange={handleInputChange}
              disabled={Boolean(editData)}
              className={`ui-select-field ${
                errors.zoneId ? "border-brand-error" : ""
              } ${editData ? "cursor-not-allowed bg-gray-100" : ""}`}
            >
              <option value="">Select a zone</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.zone_name}
                </option>
              ))}
            </select>
            {errors.zoneId ? (
              <p className="ui-form-error">{errors.zoneId}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="ui-form-label">Minimum Weight</label>
              <input
                type="text"
                name="minWeight"
                value={formData.minWeight}
                onChange={handleInputChange}
                className={fieldClass("minWeight")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.minWeight ? (
                <p className="ui-form-error">{errors.minWeight}</p>
              ) : null}
            </div>
            <div>
              <label className="ui-form-label">Maximum Weight</label>
              <input
                type="text"
                name="maxWeight"
                value={formData.maxWeight}
                onChange={handleInputChange}
                className={fieldClass("maxWeight")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.maxWeight ? (
                <p className="ui-form-error">{errors.maxWeight}</p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="ui-form-label">Builty Rate</label>
              <input
                type="text"
                name="builtyRate"
                value={formData.builtyRate}
                onChange={handleInputChange}
                className={fieldClass("builtyRate")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.builtyRate ? (
                <p className="ui-form-error">{errors.builtyRate}</p>
              ) : null}
            </div>
            <div>
              <label className="ui-form-label">Coach Rate</label>
              <input
                type="text"
                name="coachRate"
                value={formData.coachRate}
                onChange={handleInputChange}
                className={fieldClass("coachRate")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.coachRate ? (
                <p className="ui-form-error">{errors.coachRate}</p>
              ) : null}
            </div>
          </div>

          <div>
            <label className="ui-form-label">Adda Delivery</label>
            <input
              type="text"
              name="addaDelivery"
              value={formData.addaDelivery}
              onChange={handleInputChange}
              className={fieldClass("addaDelivery")}
              placeholder="e.g., 1, 2, 3.4"
            />
            {errors.addaDelivery ? (
              <p className="ui-form-error">{errors.addaDelivery}</p>
            ) : null}
          </div>
        </div>
      </section>
    </UiModal>
  );
}
